import { Link } from 'react-router-dom';
import { Plus, EyeOff, Heart } from 'lucide-react';
import { PromoBadge, PromoInfo } from './PromoBadge';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const ProductCard = ({ product, compact = false }) => {
  const { addToCart } = useCart();
  const { isAuthenticated, user } = useAuth();
  // B2B gate (Phase 35): wholesale prices are reserved for **approved** customers.
  // Guests AND pending/in-review accounts see the locked badge instead.
  const canSeeWholesale = isAuthenticated && user?.status === 'approved';
  const { isFavorite, toggleFavorite } = useFavorites();
  const { t } = useLanguage();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
    toast.success(t('product_added_to_cart', { name: product.name }));
  };

  const handleToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error(t('product_login_for_favorites'));
      return;
    }
    toggleFavorite(product.id);
  };

  const liked = isFavorite(product.id);

  return (
    <Link 
      to={`/produit/${product.id}`} 
      className="group block card-surface rounded-2xl p-2.5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-sand rounded-xl">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 rounded-xl"
          loading="lazy"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=400';
          }}
        />
        
        {/* Promo badge */}
        {product.promo && <PromoBadge product={product} className="absolute top-2 right-2 z-10" />}
        {/* Stock badges */}
        {product.preorder_enabled ? (
          <div className="absolute top-2 left-2 bg-[color:var(--color-primary)] text-white text-[10px] px-2.5 py-0.5 uppercase tracking-wider font-bold rounded-full shadow-sm" data-testid={`preorder-badge-${product.id}`}>
            {t('product_preorder_badge')}
          </div>
        ) : product.stock <= 0 && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] px-2.5 py-0.5 uppercase tracking-wider font-bold rounded-full shadow-sm">
            {t('product_out_of_stock')}
          </div>
        )}
        {product.stock > 0 && product.stock < 5 && (
          <div className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] px-2.5 py-0.5 uppercase tracking-wider font-bold rounded-full shadow-sm">
            {t('product_last_pieces')}
          </div>
        )}

        {/* Favorite button */}
        <button
          onClick={handleToggleFavorite}
          className={`absolute top-2 right-2 w-8 h-8 flex items-center justify-center transition-all duration-300 ${
            liked 
              ? 'text-red-500' 
              : 'text-heading/50 bg-white/70 rounded-full hover:text-red-400 opacity-0 group-hover:opacity-100'
          } ${liked ? 'opacity-100' : ''}`}
          data-testid={`favorite-btn-${product.id}`}
        >
          <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
        </button>

        {/* Quick add button */}
        {product.stock > 0 && (
          <button
            onClick={handleAddToCart}
            className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-[color:var(--color-primary)] text-white shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-[color:var(--color-primary-hover)] active:scale-95"
            data-testid={`add-to-cart-${product.id}`}
          >
            <Plus className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Info */}
      <div className="pt-3 px-1 pb-1">
        <p className="inline-block max-w-full text-[10px] font-bold uppercase tracking-wider text-[color:var(--color-emerald)] bg-[color:var(--color-emerald-light)] px-2 py-0.5 rounded-md mb-1.5 truncate">{product.brand}</p>
        <h3 className="text-heading text-sm font-semibold mb-2 truncate group-hover:text-[color:var(--color-primary)] transition-colors leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {product.name}
        </h3>
        
        {/* Price section */}
        <div className="flex items-end justify-between">
          <div>
            {canSeeWholesale ? (
              <div className="space-y-0.5 bg-sand rounded-lg px-2 py-1.5" data-testid={`price-tiers-${product.id}`}>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-muted text-[10px] uppercase tracking-wider w-9">{t('tier_detail')}</span>
                  {product.price_detail_original && <span className="text-muted text-[11px] line-through">{product.price_detail_original}€</span>}
                  <span className={`text-base font-extrabold ${product.promo ? 'text-red-400' : 'text-[color:var(--color-primary)]'}`}>{product.price_detail}€</span>
                </div>
                {product.price_gros && product.price_gros < product.price_detail && (
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-muted text-[10px] uppercase tracking-wider w-9">{t('tier_wholesale')}</span>
                    {product.price_gros_original && <span className="text-muted text-[11px] line-through">{product.price_gros_original}€</span>}
                    <span className={`text-sm font-bold ${product.promo ? 'text-red-400' : 'text-[color:var(--color-emerald)]'}`}>{product.price_gros}€</span>
                  </div>
                )}
                <PromoInfo product={product} showOld={false} className="mt-0.5" />
              </div>
            ) : (
              <>
                <span className={`text-base font-extrabold ${product.promo ? 'text-red-400' : 'text-[color:var(--color-primary)]'}`}>{product.price_detail}€</span>
                <PromoInfo product={product} className="mt-0.5" />
              </>
            )}
          </div>
          
          {!canSeeWholesale && (
            <div 
              className="flex items-center gap-1 text-[color:rgb(var(--primary-rgb)/0.70)]"
              data-testid="price-locked"
              onClick={(e) => e.preventDefault()}
            >
              <EyeOff className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase tracking-wider">{t('product_pro')}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
